import { type Parametros, composicaoPreco } from '@precificador/shared/pricing.js';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { ItemRodada } from '../api/tipos';
import { formatarCusto, formatarPct, formatarPeso, formatarReais, formatarReaisKg } from '../lib/formatos';
import { Aviso } from './Aviso';

function Linha({
  rotulo,
  detalhe,
  cheia,
  desconto,
  tipo = 'normal',
}: {
  rotulo: ReactNode;
  detalhe?: ReactNode;
  cheia: ReactNode;
  desconto: ReactNode;
  tipo?: 'normal' | 'total' | 'resultado';
}) {
  return (
    <tr className={clsx('border-b border-linha-suave last:border-0', tipo === 'total' && 'bg-fundo font-medium', tipo === 'resultado' && 'bg-acao-clara font-semibold')}>
      <td className="py-1.5 pr-3 pl-2">
        {rotulo}
        {detalhe && <span className="ml-1.5 text-[12px] font-normal text-tinta-2">{detalhe}</span>}
      </td>
      <td className="numero py-1.5 pr-3 text-right">{cheia}</td>
      <td className="numero py-1.5 pr-2 text-right">{desconto}</td>
    </tr>
  );
}

const menos = (v: number) => `− ${formatarReaisKg(v)}`;

/**
 * Composição do preço de uma unidade e margem de contribuição (MC), na tabela cheia e com o desconto máximo.
 * Calculada com o mesmo módulo do backend (shared/pricing.js).
 */
export function ComposicaoPrecoItem({ item, parametros }: { item: ItemRodada; parametros: Parametros }) {
  const c = composicaoPreco({
    custoKg: item.custoKg,
    peso: item.peso,
    parametros,
    precoUnit: item.precoUnit,
    acrescimoUnit: item.acrescimoUnit,
    negociacaoPct: item.negociacaoPct,
  });
  if (!c) return <Aviso tipo="info">Sem preço calculado para este item.</Aviso>;

  const { cheia, desconto } = c;
  const precoDesconto = cheia.receita * (1 - c.negociacaoPct / 100) + c.acrescimo;
  const custosKg = c.custosKg.filter((k) => k.porKg > 0);
  const variaveis = cheia.variaveis.map((v, i) => ({ ...v, valorDesconto: desconto.variaveis[i]!.valor })).filter((v) => v.pct > 0);

  return (
    <div className="flex flex-col gap-4">
      {item.situacao === 'mantido' && (
        <Aviso tipo="aviso" titulo="Preço mantido (manter preços atuais)">
          O preço calculado seria {formatarReais(item.precoCalculado)}. Como ficou diferente do atual, o preço de {formatarReais(item.precoUnit)} foi mantido e
          a margem abaixo já considera o preço mantido.
        </Aviso>
      )}
      <p className="text-tinta-2">
        Valores por unidade ({formatarPeso(item.peso)} kg). A <strong className="text-tinta">margem de contribuição</strong> é o que sobra do preço
        depois do custo (produto + valores por kg somados ao custo) e dos percentuais sobre a venda. Com desconto máximo ({formatarPct(c.negociacaoPct)}), o alvo é a
        margem líquida de {formatarPct(item.margemPct)}.
      </p>
      <table className="w-full text-left text-[13px]">
        <thead className="border-b border-linha text-[12px] text-tinta-2">
          <tr>
            <th className="py-2 pl-2 font-medium" />
            <th className="py-2 pr-3 text-right font-medium">Tabela cheia</th>
            <th className="py-2 pr-2 text-right font-medium">Com desconto máx.</th>
          </tr>
        </thead>
        <tbody>
          <Linha rotulo="Preço de venda" tipo="total" cheia={formatarReais(c.precoUnit)} desconto={formatarReais(precoDesconto)} />
          {c.acrescimo > 0 && (
            <Linha rotulo="Acréscimo por unidade" detalhe="(repasse: não entra na margem)" cheia={menos(c.acrescimo)} desconto={menos(c.acrescimo)} />
          )}
          {c.acrescimo > 0 && <Linha rotulo="Receita do produto" tipo="total" cheia={formatarReaisKg(cheia.receita)} desconto={formatarReaisKg(desconto.receita)} />}
          <Linha
            rotulo="Custo do produto"
            detalhe={`${formatarCusto(item.custoKg)} /kg`}
            cheia={menos(c.custoProduto)}
            desconto={menos(c.custoProduto)}
          />
          {custosKg.map((k) => (
            <Linha key={k.chave} rotulo={k.rotulo} detalhe={`${formatarReaisKg(k.porKg)} /kg · somado ao custo`} cheia={menos(k.valor)} desconto={menos(k.valor)} />
          ))}
          {custosKg.length > 0 && <Linha rotulo="Custo total" tipo="total" cheia={menos(c.custoTotal)} desconto={menos(c.custoTotal)} />}
          {variaveis.map((v) => (
            <Linha key={v.chave} rotulo={v.rotulo} detalhe={`${formatarPct(v.pct)} do preço`} cheia={menos(v.valor)} desconto={menos(v.valorDesconto)} />
          ))}
          <Linha
            rotulo="Margem de contribuição"
            tipo="resultado"
            cheia={<span className={clsx(cheia.mc < 0 && 'text-erro')}>{formatarReaisKg(cheia.mc)}</span>}
            desconto={<span className={clsx(desconto.mc < 0 && 'text-erro')}>{formatarReaisKg(desconto.mc)}</span>}
          />
          <Linha rotulo="MC %" detalhe="sobre a receita" cheia={formatarPct(cheia.mcPct)} desconto={formatarPct(desconto.mcPct)} />
          <Linha rotulo="MC por kg" cheia={formatarReaisKg(cheia.mcKg)} desconto={formatarReaisKg(desconto.mcKg)} />
        </tbody>
      </table>
      <p className="text-[12px] text-tinta-2">
        Percentuais variáveis somados: {formatarPct(cheia.variaveis.reduce((s, v) => s + v.pct, 0))} ({formatarReaisKg(cheia.totalVariaveis)} na tabela
        cheia). Valores zerados não aparecem.
      </p>
    </div>
  );
}
